import React, {useEffect, useState} from 'react'
import _ from 'lodash'
import {useGesture} from '@use-gesture/react'
import {Icon, Input, Popup} from 'semantic-ui-react'
import Slider from 'react-slider'
import {FaLessThanEqual} from 'react-icons/fa'
import './style.css'

const _defaultMin = 0
const _defaultMax = 1
const _defaultStep = 0.1
const _defaultValue = 0
const _defaultName = 'p'

export interface IParamData {
  name: string
  value: number
  min: number
  max: number
  step: number
}

export interface IParamProps extends IParamData {
  onChange?: (data: IParamData) => void
  onDelete?: () => void
}

export interface IExpressionParamsProps {
  actions?: React.ReactNode
  paramData?: {[key: string]: IParamData}
  onChange?: (data: {[key: string]: IParamData}) => void
}

type IParamActionProps = React.HTMLAttributes<HTMLDivElement>

function SaveParam(props: IParamActionProps) {
  return (
    <Popup
      size='tiny'
      content='save param'
      trigger={
        <div
          className='param-action save'
          {...props}
        >
          <Icon name='save' />
        </div>
      }
    />
  )
}

function DeleteParam(props: IParamActionProps) {
  return (
    <Popup
      size='tiny'
      content='delete param'
      trigger={
        <div
          className='param-action delete'
          {...props}
        >
          <Icon name='trash' />
        </div>
      }
    />
  )
}

export function Param(props: IParamProps) {
  const {onChange, onDelete} = props

  // numerical state
  const [state, setState] = useState<IParamData>({
    min: _defaultMin,
    max: _defaultMax,
    step: _defaultStep,
    value: _defaultValue,
    name: '',
  })

  const [name, setName] = useState(_.toString(_.get(props, 'name', _defaultName)))
  const [value, setValue] = useState(_.get(props, 'value', _defaultValue))
  const [isEditing, setIsEditing] = useState(false)
  const [inputMin, setInputMin] = useState<string | undefined>(
    _.toString(_.get(props, 'min', _defaultMin))
  )
  const [inputMax, setInputMax] = useState<string | undefined>(
    _.toString(_.get(props, 'max', _defaultMax))
  )
  const [inputStep, setInputStep] = useState<string | undefined>(
    _.toString(_.get(props, 'step', _defaultStep))
  )

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      min: props.min !== undefined ? props.min : prev.min,
      max: props.max !== undefined ? props.max : prev.max,
      step: props.step !== undefined ? props.step : prev.step,
      value: props.value !== undefined ? props.value : prev.value,
    }))
  }, [props.min, props.max, props.step, props.value])

  useEffect(() => {
    if (!state) return
    if (value !== state.value) setValue(state.value)
  }, [state])

  // useEffect(handleSave, [
  //     inputMin,
  //     inputMax,
  //     inputStep,
  //     // value,
  //     name,
  // ]);

  useGesture(
    {
      onKeyDown: ({event}) => {
        // const t = event.target as HTMLInputElement;
        if (isEditing && event.code === 'Enter') {
          event.stopPropagation()
          event.preventDefault()
          handleSave()
        }
      },
    },
    {
      target: window,
      eventOptions: {
        passive: false,
      },
    }
  )

  function tryNum(value: string | undefined, defaultValue: number): number {
    if (value === undefined) return defaultValue
    if (_.isNumber(value)) return value
    if (value.length === 0) return defaultValue
    const res = Number.parseFloat(value)
    if (Number.isNaN(res)) return defaultValue
    return res
  }

  function cleanName(name: string): string {
    name = _.deburr(name)
    name = _.replace(name, /\s+/, '')
    return name
  }

  function handleSave() {
    const _min = tryNum(inputMin, _defaultMin)
    const _max = tryNum(inputMax, _defaultMax)
    const _step = tryNum(inputStep, _defaultStep)
    const _name = cleanName(name === undefined ? _defaultName : name || _defaultName)
    const _state: IParamData = {
      min: _min,
      max: _max,
      step: _step,
      name: _name,
      value: _.clamp(value, _min, _max),
    }
    setState(_state)
    if (onChange) onChange(_state)
    setIsEditing(false)
  }

  const handleDelete: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation()
    if (onDelete) onDelete()
  }

  return (
    <div className='param'>
      {isEditing ?
        <div className='var edit'>
          <div className='value'>
            <div>
              range:
              <Input
                className='param-input'
                value={inputMin !== undefined ? inputMin : ''}
                onChange={(_e, {value}) => setInputMin(value)}
              />
              <FaLessThanEqual className='icon' />
              <Input
                className='param-input'
                value={name || ''}
                onChange={(_e, {value}) => setName(cleanName(value))}
              />
              <FaLessThanEqual className='icon' />
              <Input
                className='param-input'
                value={inputMax !== undefined ? inputMax : ''}
                onChange={(_e, {value}) => setInputMax(value)}
              />
            </div>
            <div>
              step:
              <Input
                className='param-input'
                value={inputStep !== undefined ? inputStep : ''}
                onChange={(_e, {value}) => setInputStep(value)}
              />
            </div>
          </div>
          <div className='actions'>
            <SaveParam onClick={handleSave} />
            <DeleteParam onClick={handleDelete} />
          </div>
        </div>
      : <>
          <div
            className='var'
            onClick={() => setIsEditing(true)}
          >
            <div className='value'>
              {name} = {value}
            </div>
            <DeleteParam onClick={handleDelete} />
          </div>
          <Slider
            min={state?.min !== undefined ? state.min : _defaultMin}
            max={state?.max !== undefined ? state.max : _defaultMax}
            step={state?.step || _defaultStep}
            value={value}
            onChange={(value) => setValue(value)}
            onAfterChange={handleSave}
            // marks={[0, 0.25, 0.5, 0.75, 1]}
          />
        </>
      }
    </div>
  )
}

export function ExpressionParams(props: IExpressionParamsProps) {
  const {actions, paramData, onChange} = props

  const [params, setParams] = useState<IParamProps[]>([])

  useEffect(() => {
    const newP = _.chain(paramData)
      .toPairs()
      .map(([_k, p]) => p as IParamProps)
      .value()
    if (_.isEqual(params, newP)) return
    setParams(newP)
  }, [paramData])

  useEffect(() => {
    if (onChange)
      onChange(
        _.chain(params)
          .map((p) => [p.name, p])
          .fromPairs()
          .value()
      )
  }, [params])

  function addParam() {
    setParams((prev) => {
      const prevKeys = _.chain(prev)
        .map((v) => v.name)
        .uniq()
        .value()
      let i = 1
      let n: string | undefined
      while (true) {
        n = `${_defaultName}_${i}`
        if (_.includes(prevKeys, n)) {
          i++
          continue
        }
        break
      }
      return [
        ...prev,
        {
          name: n,
          value: _defaultValue,
          min: _defaultMin,
          max: _defaultMax,
          step: _defaultStep,
        },
      ]
    })
  }

  function handleOnChange(key: number, data: IParamData) {
    setParams((prev) => {
      if (prev[key] === data) return prev
      const res = [...prev]
      res[key] = data
      return res
    })
  }

  function handleOnDelete(key: number) {
    setParams((prev) => _.without(prev, prev[key]))
  }

  return (
    <div className='params'>
      <div className='items'>
        {_.chain(params)
          .map((p, k) => (
            <Param
              key={p.name}
              {...p}
              onChange={(data) => handleOnChange(k, data)}
              onDelete={() => handleOnDelete(k)}
            />
          ))
          .value()}
      </div>
      <div className='actions'>
        {actions ? actions : null}
        <div
          className='add-param'
          onClick={addParam}
        >
          + param
        </div>
      </div>
    </div>
  )
}
